import type { CSSProperties, FC, FormEvent, ReactNode } from 'react'
import type { AccessRole } from '@/types'

import { useState } from 'react'
import { ArrowRight, CircleAlert, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Accent } from '@/components/landing/section-heading'

import { accessRoles } from '@/constants/landing'

interface AccessSectionProps {
    role: AccessRole
    onRoleChange: (role: AccessRole) => void
}

type Status = 'idle' | 'loading' | 'success' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const orbits = [
    { size: 'size-[260px]', duration: '22s', body: 'size-3 bg-brand' },
    { size: 'size-[400px]', duration: '34s', body: 'size-4 bg-ink' },
    { size: 'size-[560px]', duration: '48s', body: 'size-2.5 bg-signal' },
    { size: 'size-[720px]', duration: '64s', body: 'size-3 bg-[#c4c8d6]' }
]

export const AccessSection: FC<AccessSectionProps> = ({ role, onRoleChange }): ReactNode => {
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const trimmed = email.trim()

        if (!EMAIL_PATTERN.test(trimmed)) {
            setStatus('error')
            setMessage("Please enter a valid email address.")
            return
        }

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/early-access', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: trimmed, role, company })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string } | null

            if (!response.ok || !data?.ok) {
                setStatus('error')
                setMessage(data?.message ?? "Something went wrong. Please try again.")
                return
            }

            setStatus('success')
            setMessage('')
        }
        catch {
            setStatus('error')
            setMessage("We could not reach the server. Check your connection and try again.")
        }
    }

    return (
        <section id='access' className='scroll-mt-20 px-3 pb-6 pt-12 sm:px-4 sm:pb-8'>
            <Reveal>
                <div className='relative isolate mx-auto max-w-[1400px] overflow-hidden rounded-[40px] border border-border/80 bg-white elevate-lg'>
                    <div
                        aria-hidden='true'
                        className='absolute inset-0 -z-10 bg-grid opacity-80 [mask-image:radial-gradient(ellipse_at_80%_50%,black_20%,transparent_70%)]'
                    />

                    <div aria-hidden='true' className='pointer-events-none absolute -right-[260px] top-1/2 -z-10 hidden -translate-y-1/2 [perspective:1200px] md:block lg:-right-[160px]'>
                        <div className='orbit-plane relative flex size-[760px] items-center justify-center'>
                            {orbits.map(orbit => (
                                <div
                                    key={orbit.size}
                                    className={cn('orbit-ring absolute rounded-full border border-[#dfe2ea]', orbit.size)}
                                    style={{ '--orbit-duration': orbit.duration } as CSSProperties}
                                >
                                    <span
                                        className={cn('orbit-body absolute left-1/2 top-0 rounded-full elevate', orbit.body)}
                                        style={{ '--orbit-duration': orbit.duration } as CSSProperties}
                                    />
                                </div>
                            ))}
                        </div>

                        <span className='absolute left-1/2 top-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_32%_28%,#9aa8ff,#2f49ff_55%,#1a2ab8)] shadow-[0_30px_60px_-20px_rgba(47,73,255,0.6)]' />
                    </div>

                    <div className='grid gap-10 px-6 py-16 sm:px-12 sm:py-20 lg:grid-cols-2 lg:px-16 lg:py-24'>
                        <div className='max-w-xl'>
                            <p className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground'>
                                <span className='text-brand'>08</span>
                                <span className='h-px w-8 bg-border' />
                                Early access
                            </p>

                            <h2 className='mt-5 text-balance font-heading text-[clamp(2.6rem,5.6vw,4.8rem)] font-semibold leading-[0.98] tracking-[-0.045em] text-ink'>
                                Put the universe <Accent className='text-brand'>in motion.</Accent>
                            </h2>

                            <p className='mt-5 max-w-md text-lg leading-relaxed text-muted-foreground'>
                                Join the early-access list. Students get in free, forever, and schools get a guided pilot with their own classes.
                            </p>

                            {status === 'success' ? (
                                <div role='status' className='mt-10 flex items-start gap-4 rounded-3xl border border-brand/20 bg-brand-soft p-6'>
                                    <CircleCheck className='mt-0.5 size-6 shrink-0 text-brand' />

                                    <div>
                                        <p className='text-lg font-semibold text-ink'>You are on the list.</p>
                                        <p className='mt-1 text-[15px] leading-relaxed text-muted-foreground'>
                                            We will email <span className='font-medium text-ink'>{email.trim()}</span> as soon as your spot opens. Until then, keep playing with the lab above.
                                        </p>

                                        <button
                                            type='button'
                                            onClick={() => {
                                                setStatus('idle')
                                                setEmail('')
                                            }}
                                            className='mt-4 text-sm font-medium text-brand underline-offset-4 hover:underline'
                                        >
                                            Add another email
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} noValidate className='mt-10'>
                                    <fieldset>
                                        <legend className='text-sm font-medium text-ink'>
                                            I am joining as
                                        </legend>

                                        <div className='mt-3 inline-flex flex-wrap gap-1 rounded-full border border-border bg-secondary p-1'>
                                            {accessRoles.map(option => (
                                                <label
                                                    key={option.value}
                                                    className={cn(
                                                        'cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition-all has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring',
                                                        role === option.value ? 'bg-white text-ink shadow-[0_1px_2px_rgba(13,14,20,0.08),0_4px_12px_-4px_rgba(13,14,20,0.18)]' : 'text-muted-foreground hover:text-ink'
                                                    )}
                                                >
                                                    <input
                                                        type='radio'
                                                        name='role'
                                                        value={option.value}
                                                        checked={role === option.value}
                                                        onChange={() => onRoleChange(option.value)}
                                                        className='sr-only'
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </fieldset>

                                    <div className='mt-6'>
                                        <label htmlFor='access-email' className='text-sm font-medium text-ink'>
                                            Email address
                                        </label>

                                        <div className='mt-3 flex flex-col gap-2 rounded-[22px] border border-border bg-white p-1.5 elevate transition-colors focus-within:border-brand/50 sm:flex-row sm:rounded-full'>
                                            <input
                                                id='access-email'
                                                type='email'
                                                name='email'
                                                autoComplete='email'
                                                inputMode='email'
                                                required
                                                placeholder="you@school.edu"
                                                value={email}
                                                onChange={event => {
                                                    setEmail(event.target.value)
                                                    if (status === 'error') setStatus('idle')
                                                }}
                                                aria-invalid={status === 'error'}
                                                aria-describedby={status === 'error' ? 'access-error' : undefined}
                                                className='h-12 min-w-0 flex-1 rounded-full bg-transparent px-4 text-[15px] text-ink outline-none placeholder:text-muted-foreground/70 focus-visible:outline-none'
                                            />

                                            <button
                                                type='submit'
                                                disabled={status === 'loading'}
                                                className='group inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-6 text-[15px] font-medium text-white transition-colors hover:bg-ink/85 disabled:cursor-wait disabled:opacity-70'
                                            >
                                                {status === 'loading' ? (
                                                    <>
                                                        <LoaderCircle className='size-4 animate-spin' />
                                                        Saving your spot
                                                    </>
                                                ) : (
                                                    <>
                                                        Request access
                                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                            <label htmlFor='access-company'>Company</label>
                                            <input
                                                id='access-company'
                                                type='text'
                                                name='company'
                                                tabIndex={-1}
                                                autoComplete='off'
                                                value={company}
                                                onChange={event => setCompany(event.target.value)}
                                            />
                                        </div>

                                        {status === 'error' && message && (
                                            <p id='access-error' role='alert' className='mt-3 flex items-center gap-2 text-sm text-destructive'>
                                                <CircleAlert className='size-4 shrink-0' />
                                                {message}
                                            </p>
                                        )}

                                        <p className='mt-4 text-[13px] text-muted-foreground'>
                                            No spam. One email when your spot is ready, and you can leave the list at any time.
                                        </p>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </Reveal>
        </section>
    )
}
